import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, PencilSimple, Trash, BookOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import GstinLookupField from "@/components/GstinLookupField";
import StateSelector from "@/components/StateSelector";
import PageExportBar from "@/components/PageExportBar";

const emptyForm = {
  name: "", contact_person: "", gstin: "", phone: "", email: "", address: "",
  state: "", state_code: "", opening_balance: 0
};

export default function Customers() {
  const { activeCompanyId, can, activeStoreId } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, q]);

  const startAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setForm({ ...emptyForm, ...r }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone || !form.phone.trim()) { toast.error("Mobile Number is required"); return; }
    if (!form.address || !form.address.trim()) { toast.error("Address is required"); return; }
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer created");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this customer?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Master Data</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Customers</h1>
        </div>
        <div className="flex items-center gap-3">
          <PageExportBar
            title="Customers"
            filename={`customers-${new Date().toISOString().slice(0,10)}`}
            columns={[
              { key: "name", label: "Name" },
              { key: "gstin", label: "GSTIN" },
              { key: "phone", label: "Mobile Number" },
              { key: "email", label: "Email" },
              { key: "state", label: "State" },
              { key: "state_code", label: "State Code" },
              { key: "opening_balance", label: "Opening Balance" },
            ]}
            rows={rows}
            testidPrefix="customers"
          />
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…"
              className="pl-9 h-9 w-64" data-testid="customer-search" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startAdd} className="h-9" data-testid="customer-add-btn">
                <Plus size={15} weight="bold" className="mr-1.5" /> New Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">{editing ? "Edit Customer" : "New Customer"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <GstinLookupField
                    value={form.gstin}
                    onChange={(v) => setForm((f) => ({ ...f, gstin: v }))}
                    onResolved={(r) => {
                      setForm((f) => {
                        const patch = { ...f };
                        if (r.state) patch.state = r.state;
                        if (r.state_code) patch.state_code = r.state_code;
                        const nameFromApi = r.apply_name || r.trade_name || r.legal_name;
                        // Always overwrite when user clicks "Use this name"; else only fill if blank
                        if (r.apply_name) patch.name = r.apply_name;
                        else if (nameFromApi && !patch.name) patch.name = nameFromApi;
                        const addrFromApi = r.address && (r.pincode ? `${r.address} — ${r.pincode}` : r.address);
                        if (addrFromApi && (!patch.address || r.apply_name)) patch.address = addrFromApi;
                        return patch;
                      });
                    }}
                    label="GSTIN"
                    testid="cust-gstin"
                  />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="eyebrow">Company Name *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="eyebrow">Owner / Proprietor / Director</Label>
                  <Input
                    value={form.contact_person}
                    onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                    placeholder="Authorised person acting on behalf of the company"
                    data-testid="cust-contact-person" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Mobile Number *</Label>
                  <Input required inputMode="tel" pattern="[0-9+\-\s]{7,}"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="10-digit mobile"
                    data-testid="cust-phone" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="eyebrow">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="eyebrow">Address *</Label>
                  <Textarea required rows={2}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    data-testid="cust-address" />
                </div>
                <div className="col-span-2">
                  <StateSelector
                    state={form.state}
                    stateCode={form.state_code}
                    onChange={({ state, state_code }) => setForm((f) => ({ ...f, state, state_code }))}
                    testid="cust-state"
                  />
                </div>
                <DialogFooter className="col-span-2 mt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="cust-save">{editing ? "Update" : "Create"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Owner / Proprietor</th>
              <th>GSTIN</th>
              <th>Mobile Number</th>
              <th>State</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-muted-foreground py-10">
                No customers yet. Create your first customer to begin billing.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`cust-row-${r.id}`}>
                <td className="font-medium">{r.name}</td>
                <td className="text-[12.5px]">{r.contact_person || "—"}</td>
                <td className="font-mono text-[12.5px]">{r.gstin || "—"}</td>
                <td>{r.phone || "—"}</td>
                <td>{r.state || "—"}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  <Link to={`/ledger/customer/${r.id}`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="View Ledger" data-testid={`cust-ledger-${r.id}`}>
                      <BookOpen size={14} />
                    </Button>
                  </Link>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(r)} data-testid={`cust-edit-${r.id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  {can("customers", "delete") && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`cust-del-${r.id}`}>
                      <Trash size={14} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
